import { motion } from 'framer-motion'
import { cn } from '@/lib/utils'
import { SYMBOLS, type SlotSymbol } from '../utils/slotsLogic'

interface SymbolProps {
  symbol: SlotSymbol
  isWinning?: boolean
  size?: 'sm' | 'md' | 'lg'
}

const sizeClasses = {
  sm: 'w-[50px] h-[50px] text-2xl',
  md: 'w-[60px] h-[60px] md:w-[72px] md:h-[68px] text-3xl md:text-4xl',
  lg: 'w-[80px] h-[80px] text-5xl',
}

// Symbol-specific background colors for a more vibrant look
const symbolBackgrounds: Record<SlotSymbol, string> = {
  gun: 'from-zinc-800 via-zinc-700 to-zinc-800',
  dice: 'from-red-900/50 via-red-800/30 to-red-900/50',
  cards: 'from-blue-900/50 via-blue-800/30 to-blue-900/50',
  diamond: 'from-cyan-900/50 via-cyan-800/30 to-cyan-900/50',
  whiskey: 'from-amber-900/50 via-amber-800/30 to-amber-900/50',
  cigar: 'from-orange-900/50 via-orange-800/30 to-orange-900/50',
  wild: 'from-purple-900/60 via-purple-700/40 to-purple-900/60',
  scatter: 'from-green-900/60 via-green-700/40 to-green-900/60',
}

export function Symbol({ symbol, isWinning = false, size = 'md' }: SymbolProps) {
  const symbolInfo = SYMBOLS[symbol]
  const isSpecial = symbol === 'wild' || symbol === 'scatter'

  return (
    <motion.div
      animate={
        isWinning
          ? {
              scale: [1, 1.15, 1],
              boxShadow: [
                '0 0 0px rgba(212, 175, 55, 0)',
                '0 0 25px rgba(212, 175, 55, 0.9)',
                '0 0 0px rgba(212, 175, 55, 0)',
              ],
            }
          : {}
      }
      transition={
        isWinning
          ? {
              duration: 0.5,
              repeat: Infinity,
              ease: 'easeInOut',
            }
          : {}
      }
      className={cn(
        sizeClasses[size],
        'relative flex items-center justify-center rounded-lg',
        'select-none overflow-hidden',
        // Base styling
        'bg-gradient-to-br',
        symbolBackgrounds[symbol],
        // Border styling
        isWinning
          ? 'border-2 border-casino-gold shadow-[0_0_15px_rgba(212,175,55,0.5)]'
          : 'border border-zinc-600/50',
        // Special symbols get extra styling
        isSpecial && 'ring-1 ring-inset ring-white/10'
      )}
      title={symbolInfo.name}
    >
      {/* Inner glow for special symbols */}
      {isSpecial && (
        <div className="absolute inset-0 bg-gradient-radial from-white/10 via-transparent to-transparent" />
      )}

      {/* Symbol shadow/glow based on type */}
      <div
        className={cn(
          'absolute inset-0 rounded-lg opacity-50',
          symbol === 'wild' && 'bg-gradient-to-t from-purple-500/30 to-transparent',
          symbol === 'scatter' && 'bg-gradient-to-t from-green-500/30 to-transparent',
          symbol === 'diamond' && 'bg-gradient-to-t from-cyan-400/20 to-transparent',
        )}
      />

      {/* The emoji symbol with enhanced styling */}
      <span
        className={cn(
          'relative z-10',
          'drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)]',
          isWinning && 'drop-shadow-[0_0_8px_rgba(212,175,55,0.8)]',
          isSpecial && 'drop-shadow-[0_0_6px_rgba(255,255,255,0.4)]'
        )}
      >
        {symbolInfo.emoji}
      </span>

      {/* Shine effect overlay */}
      <div className="absolute inset-0 bg-gradient-to-br from-white/10 via-transparent to-transparent rounded-lg pointer-events-none" />

      {/* Bottom reflection */}
      <div className="absolute bottom-0 left-0 right-0 h-1/3 bg-gradient-to-t from-black/40 to-transparent pointer-events-none" />

      {/* Special badge for wild/scatter */}
      {isSpecial && (
        <div
          className={cn(
            'absolute -bottom-0.5 left-1/2 -translate-x-1/2',
            'text-[8px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-t',
            symbol === 'wild' && 'bg-purple-600 text-white',
            symbol === 'scatter' && 'bg-green-600 text-white'
          )}
        >
          {symbol === 'wild' ? 'WILD' : 'BONUS'}
        </div>
      )}
    </motion.div>
  )
}
